import React, { useEffect, useState } from "react";
import "./sidebar.css";
import axios from "axios";
import Icon from "@mdi/react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  mdiChartBox,
  mdiWallet,
  mdiStarFourPointsBox,
  mdiCart,
  mdiAccount,
  mdiInformationBox,
  mdiLogout,
} from "@mdi/js";
const REACT_APP_EXPRESS_API = process.env.REACT_APP_EXPRESS_API; 


/**
 * Props:
 * - isOpen: boolean
 * - onClose: function
 * - breakpoint: px (default 768)
 */
export default function Sidebar({ isOpen, onClose, breakpoint = 768 }) {
  const navigate = useNavigate();
  const location = useLocation();
  const path = location.pathname || "/";

  // responsif
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined" ? window.innerWidth >= breakpoint : true
  );
  
  // role untuk admin
  const [userRole, setUserRole] = useState("user");

  useEffect(() => {
    const onResize = () => setIsDesktop(window.innerWidth >= breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);

  // Fetch role
  useEffect(() => {
    const fetchUserRole = async () => {
      try {
        const res = await axios.get(`${REACT_APP_EXPRESS_API}/api/users/current`, {
          withCredentials: true,
        });
        if (res.data && res.data.user && res.data.user.role) {
          setUserRole(res.data.user.role);
        }
      } catch (err) {
        console.error("Error fetching user role", err);
      }
    };
    fetchUserRole();
  }, []);

  // lock scroll saat drawer mobile terbuka
  useEffect(() => {
    if (!isDesktop && isOpen) document.body.style.overflow = "hidden";
    else document.body.style.overflow = "auto";
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isDesktop, isOpen]);

  // tutup saat pindah route (mobile)
  useEffect(() => {
    if (!isDesktop && isOpen) onClose?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // ESC menutup (mobile)
  useEffect(() => {
    if (isDesktop || !isOpen) return;
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [isDesktop, isOpen, onClose]);

  const handleLogout = async () => {
    try {
      await axios.post(`${REACT_APP_EXPRESS_API}/api/users/logout`, {}, { withCredentials: true });
    } catch (_) {
      /* ignore */
    } finally {
      window.location.href = "/";
    }
  };

  const goto = (to) => () => navigate(to);

  // ----- NAV ITEMS + ACTIVE MATCHER -----
  const items = [
    {
      to: "/dashboard",
      label: "Dasbor",
      icon: mdiChartBox,
      // aktif juga untuk "/" (landing setelah login)
      match: (p) => p === "/" || p.startsWith("/dashboard"),
    },
    {
      to: "/anggaran",
      label: "Anggaran",
      icon: mdiWallet,
      match: (p) => p.startsWith("/anggaran"),
    },
    {
      to: "/poin",
      label: "Poin",
      icon: mdiStarFourPointsBox,
      match: (p) => p.startsWith("/poin"),
    },
    {
      to: "/transaction",
      label: "Transaksi",
      icon: mdiCart,
      match: (p) => p.startsWith("/transaction"),
    },
    {
      to: "/akun",
      label: "Akun",
      icon: mdiAccount,
      match: (p) => p.startsWith("/akun"),
    },
    {
      to: "/bantuan",
      label: "Bantuan",
      icon: mdiInformationBox,
      match: (p) => p.startsWith("/bantuan"),
    },
  ];

  if (userRole === "admin") {
    items.push({
      to: "/admin",
      label: "Admin",
      icon: mdiAccount, // You can change this icon if you prefer
      match: (p) => p.startsWith("/admin"),
    });
  }

  const isActive = (item) =>
    typeof item.match === "function"
      ? item.match(path)
      : path === item.to || path.startsWith(item.to + "/");

  return (
    <>
      {/* Backdrop untuk mobile */}
      {!isDesktop && (
        <button
          className={`backdrop ${isOpen ? "show" : ""}`}
          aria-label="Tutup menu"
          onClick={() => onClose?.()}
        />
      )}

      <aside
        className={`sidebar ${isOpen ? "open" : "closed"}`}
        role={!isDesktop ? "dialog" : "navigation"}
        aria-modal={!isDesktop && isOpen ? "true" : undefined}
        aria-label="Sidebar navigasi"
      >
        <div className="sidebar-logo">
          <img src="/Images/logob.png" alt="LacaKas" />
        </div>

        <ul className="nav-list">
          {items.map((it) => (
            <li
              key={it.to}
              className={isActive(it) ? "active" : ""}
              onClick={goto(it.to)}
              aria-current={isActive(it) ? "page" : undefined}
            >
              <Icon path={it.icon} size={1} />
              <span>{it.label}</span>
            </li>
          ))}
        </ul>

        <button className="logout-button" onClick={handleLogout}>
          <Icon path={mdiLogout} size={1} />
          <span>Keluar</span>
        </button>
      </aside>
    </>
  );
}
